import React, { useState } from "react";
import { Avatar, Modal, notification, Steps } from "antd";
import { MainContainer } from "./styles";
import { defaultValues } from "./defaultValues";
import { EnterpriseRequest } from "../../../../types/enterprise";
import { enterpriseService } from "../../../../services/modules/enterprise";
import { CreateEnterpriseModalProps, Form, Step } from "./types";
import { enterpriseService as enterpriseMakeService } from "../../../../services/make/modules/enterpriseMake";
import ThemeColors from "../../../../themes/colors";
import Typography from "../../../../components/Typography";
import { Column, Row } from "../../../../themes/globalStyle";
import Button from "../../../../components/Button";
import CustomTextArea from "../../../../components/TextArea";
import TextInput from "../../../../components/TextInput";

const EnterpriseModal: React.FC<CreateEnterpriseModalProps> = ({
  isModalOpen,
  setIsModalOpen,
  onCreate,
}): JSX.Element => {
  const [form, setForm] = useState<Form>(defaultValues);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [current, setCurrent] = useState(0);

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const getNewAvatar = () => {
    setForm((prev) => ({ ...prev, avatar: Math.random() }));
  };

  const getMainAccountId = () => {
    const token = localStorage.getItem("coprodux-token");

    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.mainAccountId;
      } catch (error) {
        console.error("Invalid token format or decoding error:", error);
        return;
      }
    }

    return;
  };

  const mapFormToRequest = () => {
    const mainAccountId = getMainAccountId();

    if (mainAccountId) {
      return {
        avatar: form.avatar.toString(),
        company_name: form.name,
        email: `${form.name}-${mainAccountId}}@email.com`.replace(/\s+/g, ""),
        friendly_name: form.name,
        status: "teste",
        website: form.website || "",
        main_account_id: mainAccountId,
        benefits: form.benefits || "",
        differentiators: form.differentiators || "",
        instagram: form.instagram || "",
        moreInfo: form.moreInfo || "",
        productsAndServices: form.productsAndServices || "",
        sector: form.sector || "",
      } as EnterpriseRequest;
    }
  };

  const handleCreateEnterprise = () => {
    setIsLoading(true);

    const mapped = mapFormToRequest();

    if (mapped) {
      enterpriseService
        .createEnterprise(mapped)
        .then(() => {
          notification.success({
            message: "Sucesso",
            description: "Empresa criada com sucesso.",
          });

          onCreate();

          enterpriseMakeService.createEnterprise(mapped).catch((error) => {
            console.error("Erro no Make:", error);
          });
        })
        .finally(() => setIsLoading(false));
    }
  };

  const StepIcon = (props: { step: string; stepKey: number }) => (
    <div
      style={{
        width: "1.5rem",
        height: "1.5rem",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: "100%",
        backgroundColor:
          current >= props.stepKey
            ? ThemeColors.Purple500
            : ThemeColors.Purple200,
      }}
    >
      <Typography text={props.step} size="tiny" color="Bg300" weight={500} />
    </div>
  );

  const onChange = (value: number) => {
    setCurrent(value);
  };

  const steps: Step = {
    0: {
      title: "Sobre o seu negócio",
      placeholder:
        "Com o nome, podemos buscar informações sobre o projeto na web.",
      field: "1",
      element: (
        <Column>
          <Row>
            <div style={{ width: 56, height: 56 }}>
              <Avatar
                src={`https://api.dicebear.com/9.x/shapes/svg?seed=${form.avatar}`}
                size={56}
                style={{ background: ThemeColors.Purple100 }}
              />
            </div>
            <Button
              size="middle"
              borderRadius="0.5rem"
              style={{ width: "fit-content" }}
              backgroundColor={ThemeColors.Black500}
              onClick={getNewAvatar}
            >
              <Typography
                text="Gerar novo avatar"
                size="tiny"
                color="Bg300"
                weight={400}
              />
            </Button>
          </Row>
          <Column>
            <TextInput
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="Nome da Empresa"
              value={form.name}
            />
          </Column>
        </Column>
      ),
    },
    1: {
      title: "A Empresa tem um site?",
      placeholder:
        "Com o site, podemos acessar os detalhes do projeto e salvar as informações importantes.",
      field: "2",
      element: (
        <TextInput
          onChange={(e) =>
            setForm((prev) => ({ ...prev, website: e.target.value }))
          }
          value={form.website}
          placeholder="Website"
        />
      ),
    },
    2: {
      title: "E um perfil do Instagram?",
      placeholder:
        "Usaremos para captar o tom e voz da marca, frequência de posts e outras informações importantes.",
      field: "3",
      element: (
        <TextInput
          onChange={(e) =>
            setForm((prev) => ({ ...prev, instagram: e.target.value }))
          }
          value={form.instagram}
          placeholder="Instagram"
        />
      ),
    },
    3: {
      title: "Qual é o setor?",
      placeholder:
        "Para análise da concorrência e também para oferecer insights personalizados.",
      field: "4",
      element: (
        <Column>
          <TextInput
            onChange={(e) =>
              setForm((prev) => ({ ...prev, sector: e.target.value }))
            }
            value={form.sector}
            placeholder="Setor"
          />
          <Row style={{flexWrap: 'wrap'}}>
            <Button
              borderRadius="0.5rem"
              backgroundColor={ThemeColors.Black50}
              textColor={ThemeColors.Black200}
              onClick={() => setForm((prev) => ({ ...prev, sector: "Saúde" }))}
            >
              + Saúde
            </Button>
            <Button
              borderRadius="0.5rem"
              backgroundColor={ThemeColors.Black50}
              textColor={ThemeColors.Black200}
              onClick={() => setForm((prev) => ({ ...prev, sector: "Moda" }))}
            >
              + Moda
            </Button>
            <Button
              borderRadius="0.5rem"
              backgroundColor={ThemeColors.Black50}
              textColor={ThemeColors.Black200}
              onClick={() => setForm((prev) => ({ ...prev, sector: "Beleza" }))}
            >
              + Beleza
            </Button>
            <Button
              borderRadius="0.5rem"
              backgroundColor={ThemeColors.Black50}
              textColor={ThemeColors.Black200}
              onClick={() =>
                setForm((prev) => ({ ...prev, sector: "Educação" }))
              }
            >
              + Educação
            </Button>
            <Button
              borderRadius="0.5rem"
              backgroundColor={ThemeColors.Black50}
              textColor={ThemeColors.Black200}
              onClick={() =>
                setForm((prev) => ({ ...prev, sector: "Entretenimento" }))
              }
            >
              + Entretenimento
            </Button>
          </Row>
        </Column>
      ),
    },
    4: {
      title: "E quais os diferenciais?",
      placeholder:
        "Destaque o torna a marca única e como ela se propõe a ser diferente para o cliente final.",
      field: "5",
      element: (
        <CustomTextArea
          onChange={(e) =>
            setForm((prev) => ({ ...prev, differentiators: e.target.value }))
          }
          value={form.differentiators}
          placeholder="Descreva os diferenciais"
          autoSize={{ minRows: 5, maxRows: 8 }}
        />
      ),
    },
    5: {
      title: "Me conte sobre seus produtos ou serviços",
      placeholder:
        "Além de escrever o nome, para cada um, explique sua função e o que resolve. Caso tenha muitos produtos, descreva por categorias.",
      field: "7",
      element: (
        <CustomTextArea
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              productsAndServices: e.target.value,
            }))
          }
          value={form.productsAndServices}
          placeholder="Descreva os produtos e/ou serviços"
          autoSize={{ minRows: 5, maxRows: 8 }}
        />
      ),
    },
    6: {
      title: "Mais alguma coisa?",
      placeholder:
        "Você desejaria adicionar algum comentário ou informação importante sobre o seu produto ou marca que não perguntamos?",
      field: "7",
      element: (
        <CustomTextArea
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              moreInfo: e.target.value,
            }))
          }
          value={form.moreInfo}
          placeholder="Descreva aqui mais informações"
          autoSize={{ minRows: 5, maxRows: 8 }}
        />
      ),
    },
  };

  return (
    <Modal
      destroyOnClose
      title={
        <Typography
          text="Novo Objetivo"
          size="large"
          color="Black500"
          weight={600}
        />
      }
      open={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      width={"60%"}
      centered
      footer={
        <Row style={{ justifyContent: "flex-end" }}>
          {current !== 0 && current !== 6 && (
            <Button
              size="middle"
              borderRadius="0.5rem"
              style={{ width: "fit-content" }}
              backgroundColor={ThemeColors.Black500}
              loading={isLoading}
              onClick={() => current < 6 && setCurrent((prev) => prev + 1)}
            >
              <Typography
                text="Pular"
                size="medium"
                color="Bg300"
                weight={400}
              />
            </Button>
          )}

          {current < 6 && (
            <Button
              size="middle"
              borderRadius="0.5rem"
              style={{ width: "fit-content" }}
              backgroundColor={ThemeColors.Black500}
              loading={isLoading}
              onClick={() => current < 6 && setCurrent((prev) => prev + 1)}
            >
              <Typography
                text="Avançar"
                size="medium"
                color="Bg300"
                weight={400}
              />
            </Button>
          )}

          {current === 6 && (
            <Button
              size="middle"
              borderRadius="0.5rem"
              style={{ width: "fit-content" }}
              backgroundColor={ThemeColors.Black500}
              loading={isLoading}
              onClick={handleCreateEnterprise}
            >
              <Typography
                text="Criar Empresa"
                size="medium"
                color="Bg300"
                weight={400}
              />
            </Button>
          )}
        </Row>
      }
    >
      <MainContainer>
        <Steps
          direction="vertical"
          size="small"
          current={current}
          onChange={onChange}
          items={[
            {
              icon: <StepIcon step="1" stepKey={0} />,
              title: "Geral",
            },
            {
              icon: <StepIcon step="2" stepKey={1} />,
              title: "Site",
            },
            {
              icon: <StepIcon step="3" stepKey={2} />,
              title: "Mídia Social",
            },
            {
              icon: <StepIcon step="4" stepKey={3} />,
              title: "Negócio & Marca",
            },
            {
              icon: <StepIcon step="5" stepKey={4} />,
              title: "Diferenciais",
            },
            {
              icon: <StepIcon step="6" stepKey={5} />,
              title: "Produto & Serviços",
            },
            {
              icon: <StepIcon step="7" stepKey={6} />,
              title: "Informações Adicionais",
            },
          ]}
        />
        {
          <Column style={{ gap: "2rem" }}>
            <Column>
              <Typography
                text={steps[current].title}
                size="large"
                color="Black400"
                weight={500}
              />
              <Typography
                text={steps[current].placeholder}
                size="small"
                color="Black200"
                weight={400}
              />
            </Column>

            {steps[current].element}
          </Column>
        }
      </MainContainer>
    </Modal>
  );
};

export default EnterpriseModal;
